import React, { useState, useCallback, useEffect } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { useHistory, useParams, Link } from "react-router-dom";

import Breadcrumbs from "../../../../components/Breadcrumbs";
import Container from "../../../../components/Container";
import Layout from "../../../../components/Layout";
import Content from "../../../../components/Content";
import Flex from "../../../../components/Flex";
import Label from "../../../../components/Label";
import TextBox from "../../../../components/TextBox";
import Button from "../../../../components/Button";
import Tooltip from "../../../../components/Tooltip";
import ButtonList from "../../../../components/ButtonList";
import { useFetch } from "../../../../components/useFetch";
import { useMessageToast } from "../../../../components/MessageProvider";
import { useAuthState } from "../../../../components/AuthProvider";
import Spinner from "../../../../components/Loader/Spinner";
import errorMessageHandler from "../../errorMessageHandler";

import styles from "./AddSystemNotification.module.scss";

const AddSystemNotification = () => {
  const moment = require("moment-timezone");
  const clientConfigApiDomain = process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN;
  const systemNotificationApiUrl = `${clientConfigApiDomain}/system_notifications`;

  const { id } = useParams();

  const history = useHistory();
  const { user } = useAuthState();
  const toast = useMessageToast();

  const [
    {
      data: addEditUserData,
      hasLoaded: addEditHasLoaded,
      hasError: addEditHasError,
      error
    },
    addEditFetch
  ] = useFetch();

  const [systemNotificationData, setSystemNotificationData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    const response = await fetch(`${systemNotificationApiUrl}/${id}/`);
    const data = await response.json();
    setSystemNotificationData(data);
    setIsLoading(false);
    setHasLoaded(true);
  }, [id, systemNotificationApiUrl]);

  useEffect(() => {
    if (!hasLoaded && id !== undefined) {
      fetchData();
    } else {
      // this enables the add screen to show up
      setHasLoaded(true);
    }
  }, [id, setIsLoading, hasLoaded, fetchData]);

  useEffect(() => {
    if (id === undefined && addEditHasLoaded) {
      if (addEditHasLoaded) {
        toast.success(`System Notification added successfully.`);

        history.push(`/admin/systemnotifications/${addEditUserData.id}`);
      } else if (addEditHasError) {
        if (error.response.status === 400) {
          const errorMsgArray = errorMessageHandler(error.response.data);
          errorMsgArray.forEach(msg => toast.error(msg));
        } else toast.error("Failed to add System Notification");
      }
    } else {
      if (addEditHasLoaded) {
        toast.success(`System Notification updated successfully.`);

        history.push(`/admin/systemnotifications/${addEditUserData.id}`);
      } else if (addEditHasError) {
        if (error.response.status === 400) {
          const errorMsgArray = errorMessageHandler(error.response.data);
          errorMsgArray.forEach(msg => toast.error(msg));
        } else toast.error("Failed to update System Notification");
      }
    }
  }, [
    id,
    addEditUserData,
    addEditHasLoaded,
    addEditHasError,
    toast,
    history,
    error
  ]);

  let loadedBreadcrumbs = "";
  if (isLoading) {
    loadedBreadcrumbs = "Loading...";
  } else if (hasLoaded) {
    loadedBreadcrumbs = (
      <Breadcrumbs>
        <Breadcrumbs.Item render={() => <Link to="/">Home</Link>} />
        <Breadcrumbs.Item
          render={() => <Link to="/admin">Administration</Link>}
        />
        <Breadcrumbs.Item
          render={() => (
            <Link to="/admin/systemnotifications">System Notifications</Link>
          )}
        />
        {id ? (
          <>
            <Breadcrumbs.Item
              render={() => (
                <Link to={`/admin/systemnotifications/${id}`}>
                  System Notifications: {systemNotificationData.id}
                </Link>
              )}
            />
            <Breadcrumbs.Item last render={() => "Edit"}></Breadcrumbs.Item>
          </>
        ) : (
          <Breadcrumbs.Item
            last
            render={() => "Add System Notification"}
          ></Breadcrumbs.Item>
        )}
      </Breadcrumbs>
    );
  }

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          <Flex.Item span={12}>{loadedBreadcrumbs}</Flex.Item>
          {isLoading ? (
            <Spinner />
          ) : hasLoaded ? (
            <Flex.Item span={12}>
              <Content>
                {id ? (
                  <h1>Edit System Notification: {systemNotificationData.id}</h1>
                ) : (
                  <h1>Add System Notification</h1>
                )}
              </Content>
              <div className={styles.base}>
                <div className={styles.detailsFlex}>
                  <h3>System Notification Details</h3>
                </div>
                <div className={styles.details}>
                  <Formik
                    enableReinitialize={true}
                    initialValues={{
                      id: systemNotificationData.id,
                      message: systemNotificationData.message,
                      start_date: systemNotificationData.start_date,
                      end_date: systemNotificationData.end_date,
                      created_by: systemNotificationData.created_by
                    }}
                    validationSchema={Yup.object().shape({
                      message: Yup.string().max(350),
                      start_date: Yup.string()
                        .required("Start Date field is required")
                        .matches(
                          /^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/,
                          "Invalid Date format"
                        ),
                      end_date: Yup.string()
                        .required("Start Date field is required")
                        .matches(
                          /^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/,
                          "Invalid Date format"
                        )
                    })}
                    onSubmit={(values, { setSubmitting, resetForm }) => {
                      const data = {
                        message: values.message,
                        start_date: values.start_date,
                        end_date: values.end_date,
                        updated_by: user.username,
                        created_by: values.created_by
                      };
                      let method = "";
                      let url = "";

                      if (id) {
                        data["id"] = id;
                        method = "put";
                        url = `${systemNotificationApiUrl}/${id}/`;
                      } else {
                        data["created_by"] = user.username;
                        method = "post";
                        url = `${systemNotificationApiUrl}/`;
                      }

                      addEditFetch({
                        method,
                        url,
                        data
                      });

                      setSubmitting(false);
                    }}
                  >
                    {props => {
                      const {
                        values,
                        touched,
                        errors,
                        handleChange,
                        handleBlur,
                        handleSubmit
                      } = props;

                      return (
                        <form onSubmit={handleSubmit} autoComplete="off">
                          <div>
                            {id ? (
                              <>
                                <h4>System Notification ID</h4>
                                <span>{systemNotificationData.id}</span>
                              </>
                            ) : null}
                          </div>
                          <div>
                            <TextBox
                              errorText={
                                touched.message ? errors.message : null
                              }
                              id="message"
                              label={
                                <Label
                                  error={touched.message && errors.message}
                                  htmlFor="message"
                                >
                                  <b>Message</b>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required
                              value={values.message}
                              width="33%"
                            />
                          </div>
                          <div>
                            <TextBox
                              errorText={
                                touched.start_date ? errors.start_date : null
                              }
                              id="start_date"
                              label={
                                <Label
                                  error={
                                    touched.start_date && errors.start_date
                                  }
                                  htmlFor="start_date"
                                >
                                  <b>Start Date</b>{" "}
                                  <Tooltip>
                                    <Content>
                                      <p>
                                        End Date for System Notification in
                                        YYYY-MM-DD format.
                                      </p>
                                    </Content>
                                  </Tooltip>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required
                              value={values.start_date}
                              width="33%"
                            />
                          </div>
                          <div>
                            <TextBox
                              errorText={
                                touched.end_date ? errors.end_date : null
                              }
                              id="end_date"
                              label={
                                <Label
                                  error={touched.end_date && errors.end_date}
                                  htmlFor="end_date"
                                >
                                  <b>End Date</b>
                                  <Tooltip>
                                    <Content>
                                      <p>
                                        End Date for System Notification in
                                        YYYY-MM-DD format.
                                      </p>
                                    </Content>
                                  </Tooltip>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required
                              value={values.end_date}
                              width="33%"
                            />
                          </div>
                          {id ? (
                            <>
                              <div>
                                <h4>Created By</h4>
                                <span>{systemNotificationData.created_at}</span>
                              </div>
                              <div>
                                <h4>Date Created</h4>
                                <span>
                                  {moment(systemNotificationData.created_at)
                                    .tz(user.preferences.timeZone)
                                    .format("L, LTS z")}
                                </span>
                              </div>
                              <div>
                                <h4>Edited By</h4>
                                <span>{systemNotificationData.updated_by}</span>
                              </div>
                              <div>
                                <h4>Date Updated</h4>
                                <span>
                                  {moment(systemNotificationData.updated_at)
                                    .tz(user.preferences.timeZone)
                                    .format("L, LTS z")}
                                </span>
                              </div>
                            </>
                          ) : null}
                          <ButtonList>
                            <Button primary type="submit">
                              {id ? `Save Changes` : `Save`}
                            </Button>
                            <Button
                              onClick={() => {
                                history.push("/admin/systemnotifications");
                              }}
                            >
                              Cancel
                            </Button>
                          </ButtonList>
                        </form>
                      );
                    }}
                  </Formik>
                </div>
              </div>
            </Flex.Item>
          ) : null}
        </Flex>
      </Container>
    </Layout>
  );
};

export default AddSystemNotification;
